// Utilidades da calculadora de avaliação: máscaras, validação e montagem do payload
// do CONTRATO-AVM-SITE.md §2. Sem React e sem DOM — fácil de ler e de testar.
import type { AvmConservacao, AvmIntencao, AvmPayload } from '../../lib/avm/types';

export interface AvmOpcao {
    id: number;
    nome: string;
}

export interface AvmForm {
    cidadeId: string;
    cidadeNome: string;
    bairroId: string;
    bairroNome: string;
    endereco: string;
    numero: string;
    complemento: string;
    cep: string;
    tipoId: string;
    tipoNome: string;
    areaPrivativa: string;
    areaTerreno: string;
    dormitorios: number;
    suites: number;
    banheiros: number;
    vagas: number;
    elevador: boolean;
    anoConstrucao: string;
    caracteristicas: string[];
    caracteristicaIds: number[];
    // '' = ainda não escolhido (só ocorre nos sites do gate de 7 opções, onde nada vem
    // pré-marcado e a escolha é obrigatória — ver `usaConservacao7Opcoes`).
    estadoConservacao: AvmConservacao | '';
    intencao: AvmIntencao | '';
    nome: string;
    telefone: string;
    email: string;
    precoDesejado: string;
    consentimento: boolean;
    __hp: string;
}

export type AvmErros = Partial<Record<keyof AvmForm, string>>;

export const AVM_FORM_INICIAL: AvmForm = {
    cidadeId: '',
    cidadeNome: '',
    bairroId: '',
    bairroNome: '',
    endereco: '',
    numero: '',
    complemento: '',
    cep: '',
    tipoId: '',
    tipoNome: '',
    areaPrivativa: '',
    areaTerreno: '',
    dormitorios: 0,
    suites: 0,
    banheiros: 0,
    vagas: 0,
    elevador: false,
    anoConstrucao: '',
    caracteristicas: [],
    caracteristicaIds: [],
    // "bom" já vem marcado: é o caso mais comum e evita mais um campo obrigatório
    // no meio do caminho (o visitante troca em 1 toque se for outro).
    estadoConservacao: 'bom',
    intencao: '',
    nome: '',
    telefone: '',
    email: '',
    precoDesejado: '',
    consentimento: false,
    __hp: '',
};

export const AVM_CARACTERISTICAS = [
    'piscina',
    'churrasqueira',
    'portaria 24h',
    'academia',
    'varanda',
    'área gourmet',
    'mobiliado',
    'vista livre',
    'condomínio fechado',
    'quintal',
];

export const AVM_CONSERVACOES: { valor: AvmConservacao; rotulo: string; ajuda: string }[] = [
    { valor: 'novo', rotulo: 'Novo', ajuda: 'Nunca morado ou recém-entregue' },
    { valor: 'reformado', rotulo: 'Reformado', ajuda: 'Passou por reforma recente' },
    { valor: 'bom', rotulo: 'Bom', ajuda: 'Conservado, pronto para morar' },
    { valor: 'a_reformar', rotulo: 'Precisa de reforma', ajuda: 'Vai exigir obra' },
];

/**
 * Sites que trocam as 4 opções de sempre pela lista canônica de 7 do spec 2514 (mesmos ids do
 * cadastro/edição de imóvel no CRM). Pedido de UM cliente → gate por codsite (cs2514, 21/09).
 * Nestes sites nenhuma opção vem pré-marcada e a escolha passa a ser obrigatória no passo 3
 * (ver `validarPasso`); o controle (codsite 1722) fica de fora e continua com 4 opções + "Bom".
 */
export const AVM_CONSERVACAO_7_CODSITES = [2514];

export function usaConservacao7Opcoes(codsite: number): boolean {
    return AVM_CONSERVACAO_7_CODSITES.indexOf(Number(codsite)) >= 0;
}

export const AVM_CONSERVACOES_7: { valor: AvmConservacao; rotulo: string; ajuda: string }[] = [
    { valor: 'novo', rotulo: 'Novo', ajuda: 'Construção até 1 ano' },
    { valor: 'seminovo', rotulo: 'Seminovo', ajuda: 'Construção de 1 a 5 anos' },
    { valor: 'usado_excelente', rotulo: 'Usado', ajuda: 'Excelente estado' },
    { valor: 'usado_bom', rotulo: 'Usado', ajuda: 'Bom estado' },
    { valor: 'usado_reforma', rotulo: 'Usado', ajuda: 'Necessita de reforma' },
    { valor: 'em_obra', rotulo: 'Em obra', ajuda: 'Em construção' },
    { valor: 'vazio', rotulo: 'Vazio', ajuda: 'Lotes e terrenos' },
];

/** Estado inicial do formulário — nos sites do gate de 7 opções, sem nada pré-marcado. */
export function formInicialConservacao(codsite: number): AvmForm {
    return usaConservacao7Opcoes(codsite) ? { ...AVM_FORM_INICIAL, estadoConservacao: '' } : AVM_FORM_INICIAL;
}

const semAcento = (v: string) =>
    String(v || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

/**
 * Sites que trocam a lista fixa acima pelo catálogo do CRM (Gestão › Características), em dois
 * grupos — unidade e condomínio — como no passo "O que o imóvel tem" da calculadora do CRM.
 * Pedido de UM cliente → gate por codsite (cs2514, 21/09).
 */
export const AVM_CARAC_CATALOGO_CODSITES = [2514];

export function usaCatalogoCaracteristicas(codsite: number): boolean {
    return AVM_CARAC_CATALOGO_CODSITES.indexOf(Number(codsite)) >= 0;
}

/** O app aceita no máximo 40 características por avaliação (property-valuation.public.controller). */
export const AVM_MAX_CARACTERISTICAS = 40;

export interface AvmCatalogoCaracteristicas {
    unidade: AvmOpcao[];
    condominio: AvmOpcao[];
}

const ehElevador = (nome: string) => semAcento(nome).trim() === 'elevador';

/**
 * Separa o catálogo em unidade × condomínio (o discriminador é `condominio`; a coluna `imovel`
 * é ruído no legado). O "Elevador" sai dos dois grupos: ele já é um controle próprio no passo 2
 * — mesma regra do CRM (valuationWizard.caracteristicasPorGrupo).
 */
export function separarCatalogo(lista: any): AvmCatalogoCaracteristicas {
    const itens: { id: number; nome: string; condominio: boolean }[] = (Array.isArray(lista) ? lista : [])
        .map((c: any) => ({ id: Number(c?.id), nome: String(c?.nome ?? '').trim(), condominio: !!c?.condominio }))
        .filter((c) => Number.isInteger(c.id) && c.id > 0 && c.nome && !ehElevador(c.nome))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR') || b.id - a.id);
    // O catálogo traz o mesmo nome 2× no MESMO grupo (global CodSite=0 + a cópia do tenant — no
    // cs2514 são 9: Piscina, Salão de Festas…). Um chip por nome; fica o id mais alto (o do tenant).
    // Piscina na unidade E no condomínio é legítimo: a chave inclui o grupo.
    const vistos = new Set<string>();
    const unicos = itens.filter((c) => {
        const chave = `${c.condominio ? 'c' : 'u'}|${semAcento(c.nome).replace(/\s+/g, ' ')}`;
        if (vistos.has(chave)) return false;
        vistos.add(chave);
        return true;
    });
    return {
        unidade: unicos.filter((c) => !c.condominio).map(({ id, nome }) => ({ id, nome })),
        condominio: unicos.filter((c) => c.condominio).map(({ id, nome }) => ({ id, nome })),
    };
}

/** Tipos em que a área do TERRENO faz sentido perguntar. */
export function pedeAreaTerreno(tipoNome: string): boolean {
    return /(casa|terreno|chacara|sitio|lote|fazenda|sobrado)/.test(semAcento(tipoNome));
}

/** Tipos em que perguntar "tem elevador?" faz sentido. */
export function pedeElevador(tipoNome: string): boolean {
    return /(apartamento|cobertura|flat|studio|st[uú]dio|kitnet|kitinete|sala|conjunto|loft)/.test(
        semAcento(tipoNome),
    );
}

/** Ícone (chave) do card de tipo — SVGs em AvmUI.tsx. */
export function iconeDoTipo(tipoNome: string): string {
    const n = semAcento(tipoNome);
    if (/terreno|lote|area|gleba/.test(n)) return 'terreno';
    if (/chacara|sitio|fazenda|rural/.test(n)) return 'rural';
    if (/galpao|barracao|industrial|deposito/.test(n)) return 'galpao';
    if (/sala|conjunto|comercial|loja|escritorio|predio|ponto/.test(n)) return 'comercial';
    if (/casa|sobrado|condominio|geminad/.test(n)) return 'casa';
    if (/apartamento|cobertura|flat|studio|kitnet|kitinete|loft|apto/.test(n)) return 'apartamento';
    return 'imovel';
}

/* ── tipos do imóvel: dedup + escolha dos cards ─────────────────────────────── */

/**
 * Chave de comparação de nome de tipo: minúsculas, sem acento e com espaços
 * colapsados. "APARTAMENTO", "Apartamento" e "apartamento " viram a mesma coisa.
 */
export function normalizarNomeTipo(nome: string): string {
    return semAcento(nome).replace(/\s+/g, ' ').trim();
}

/** Acima disto a lista completa migra para o combo "Outro tipo de imóvel". */
export const AVM_LIMITE_TIPOS_EM_CARDS = 8;
/** Quantos cards ficam na tela quando a lista é grande (o resto vai pro combo). */
export const AVM_MAX_CARDS_TIPO = 6;

/**
 * Tipos "comuns" que ganham card quando o tenant tem lista grande, NA ORDEM de
 * preferência. Cada entrada aceita mais de uma grafia porque o cadastro do CRM é
 * livre — na mesma `tbl_imovel_tipo` do cs1722 convivem "Casa de Condomínio" e
 * "Casa em Condomínio". A comparação é por nome INTEIRO (normalizado), nunca por
 * "contém": "Sala Comercial Aérea" não pode roubar o card de "Sala Comercial".
 */
const TIPOS_PREFERIDOS: string[][] = [
    ['apartamento'],
    ['casa'],
    ['casa em condominio', 'casa de condominio'],
    ['cobertura'],
    ['sobrado'],
    ['terreno'],
    ['sala comercial', 'sala'],
    ['loja', 'loja comercial'],
];

export interface AvmTiposPreparados {
    /** o que vira card na tela (a lista inteira quando ela é curta). */
    cards: AvmOpcao[];
    /** lista completa já SEM duplicados, na ordem original — é o que vai no combo. */
    todos: AvmOpcao[];
    /** true quando a lista é grande demais para virar card. */
    usaCombo: boolean;
}

/**
 * Prepara a lista de tipos do passo 2:
 *  1. tira os repetidos por NOME normalizado, mantendo sempre a PRIMEIRA ocorrência
 *     (é o id dela que vai no payload — nada de unir ids);
 *  2. com até AVM_LIMITE_TIPOS_EM_CARDS tipos, todos viram card (como era antes);
 *  3. passando disso, só AVM_MAX_CARDS_TIPO cards — os comuns que o tenant realmente
 *     tem, na ordem de TIPOS_PREFERIDOS, completando com os demais na ordem original —
 *     e a lista completa fica no combo "Outro tipo de imóvel".
 * Pura de propósito (sem React/DOM): dá para provar no node, em scripts/test-avm-tipos.mjs.
 */
export function prepararTipos(tipos: AvmOpcao[] | null | undefined): AvmTiposPreparados {
    const todos: AvmOpcao[] = [];
    // prefixo na chave: nome de tipo não pode colidir com "constructor"/"__proto__".
    const indicePorNome: Record<string, number> = {};

    const entrada = Array.isArray(tipos) ? tipos : [];
    for (let i = 0; i < entrada.length; i += 1) {
        const bruto = entrada[i];
        const nome = String((bruto && bruto.nome) || '').trim();
        const id = Number(bruto && bruto.id);
        if (!nome || !Number.isFinite(id)) continue;
        const chave = `t:${normalizarNomeTipo(nome)}`;
        if (chave === 't:' || indicePorNome[chave] !== undefined) continue;
        indicePorNome[chave] = todos.length;
        todos.push({ id, nome });
    }

    if (todos.length <= AVM_LIMITE_TIPOS_EM_CARDS) {
        return { cards: todos.slice(), todos, usaCombo: false };
    }

    const usados: Record<number, boolean> = {};
    const cards: AvmOpcao[] = [];

    for (let p = 0; p < TIPOS_PREFERIDOS.length && cards.length < AVM_MAX_CARDS_TIPO; p += 1) {
        const grafias = TIPOS_PREFERIDOS[p];
        for (let g = 0; g < grafias.length; g += 1) {
            const indice = indicePorNome[`t:${grafias[g]}`];
            if (indice !== undefined && !usados[indice]) {
                usados[indice] = true;
                cards.push(todos[indice]);
                break;
            }
        }
    }

    for (let i = 0; i < todos.length && cards.length < AVM_MAX_CARDS_TIPO; i += 1) {
        if (!usados[i]) {
            usados[i] = true;
            cards.push(todos[i]);
        }
    }

    return { cards, todos, usaCombo: true };
}

/* ── máscaras ───────────────────────────────────────────────────────────────── */

export const soDigitos = (v: string): string => String(v || '').replace(/\D/g, '');

/** (31) 99999-9999 — aceita fixo (10) e celular (11). */
export function mascaraTelefone(valor: string): string {
    const d = soDigitos(valor).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** "500000" → "500.000" (o prefixo R$ fica fora do input, como adorno). */
/** 00000-000 enquanto digita. */
export function mascaraCep(valor: string): string {
    const d = soDigitos(valor).slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export function mascaraMoeda(valor: string): string {
    const d = soDigitos(valor).slice(0, 12);
    if (!d) return '';
    return String(parseInt(d, 10)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Área em m²: só dígitos e um separador decimal (vírgula ou ponto). */
export function mascaraArea(valor: string): string {
    return String(valor || '')
        .replace(/[^\d.,]/g, '')
        .slice(0, 12);
}

/**
 * "70,5" → 70.5 · "1.250" → 1250 · "1.250,5" → 1250.5 · "70.5" → 70.5.
 * Regra: havendo vírgula, ela é o decimal e os pontos são milhar; só com pontos, o
 * último ponto é decimal quando NÃO separa um grupo de 3 dígitos.
 */
export function numeroOuNulo(valor: string): number | null {
    let bruto = String(valor || '').trim().replace(/\s/g, '');
    if (!bruto) return null;
    if (bruto.indexOf(',') >= 0) {
        bruto = bruto.replace(/\./g, '').replace(/,/g, '.');
    } else if (bruto.indexOf('.') >= 0) {
        const partes = bruto.split('.');
        const ultima = partes[partes.length - 1];
        bruto = ultima.length === 3 ? partes.join('') : `${partes.slice(0, -1).join('')}.${ultima}`;
    }
    const n = Number(bruto);
    return Number.isFinite(n) ? n : null;
}

export function moedaParaNumero(valor: string): number | null {
    const d = soDigitos(valor);
    if (!d) return null;
    const n = parseInt(d, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatarBRL(valor: number | null | undefined, comCentavos = false): string {
    if (valor == null || !Number.isFinite(Number(valor))) return '—';
    try {
        return Number(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: comCentavos ? 2 : 0,
            maximumFractionDigits: comCentavos ? 2 : 0,
        });
    } catch {
        return `R$ ${Math.round(Number(valor))}`;
    }
}

export const emailValido = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(v || '').trim());

/* ── validação por passo ────────────────────────────────────────────────────── */

/**
 * Sites em que a autorização (caixinha + texto do config) fica no passo 3, logo abaixo do "Estado
 * de conservação", em vez do passo 4. Pedido de UM cliente → gate por codsite (cs2514, 21/09).
 */
export const AVM_CONSENT_PASSO3_CODSITES = [2514];

export function consentimentoNoPasso3(codsite: number): boolean {
    return AVM_CONSENT_PASSO3_CODSITES.indexOf(Number(codsite)) >= 0;
}

export function validarPasso(
    passo: number,
    form: AvmForm,
    opcoes: { consentNoPasso3?: boolean; conservacaoObrigatoria?: boolean } = {},
): AvmErros {
    const erros: AvmErros = {};
    const consentPasso = opcoes.consentNoPasso3 ? 3 : 4;

    if (passo === 1) {
        // Mesma régua da calculadora do CRM (16/09): endereço COMPLETO é obrigatório — é ele
        // que o corretor usa para visitar e captar. CEP segue opcional.
        if (!form.cidadeId) erros.cidadeId = 'Escolha a cidade do imóvel.';
        if (!form.bairroNome.trim()) erros.bairroNome = 'Informe o bairro do imóvel.';
        if (!form.endereco.trim()) erros.endereco = 'Informe a rua do imóvel.';
        if (!form.numero.trim()) erros.numero = 'Informe o número.';
        const cep = soDigitos(form.cep);
        if (cep && cep.length !== 8) erros.cep = 'Confira o CEP: são 8 números.';
    }

    if (passo === 2) {
        if (!form.tipoId) erros.tipoId = 'Escolha o tipo do imóvel.';
        const area = numeroOuNulo(form.areaPrivativa);
        if (area == null) erros.areaPrivativa = 'Informe a área em m².';
        else if (area <= 0 || area > 100000) erros.areaPrivativa = 'Informe uma área válida em m².';
        if (pedeAreaTerreno(form.tipoNome) && form.areaTerreno.trim()) {
            const terreno = numeroOuNulo(form.areaTerreno);
            if (terreno == null || terreno <= 0 || terreno > 1000000) {
                erros.areaTerreno = 'Informe uma área válida em m².';
            }
        }
        if (form.anoConstrucao.trim()) {
            const ano = parseInt(form.anoConstrucao, 10);
            const limite = new Date().getFullYear() + 5;
            if (!Number.isFinite(ano) || ano < 1900 || ano > limite) {
                erros.anoConstrucao = `Use um ano entre 1900 e ${limite}.`;
            }
        }
    }

    // Só nos sites do gate de 7 opções (cs2514, 21/09): os outros ~300 sites já vêm com
    // "Bom" pré-marcado e nunca caem aqui.
    if (passo === 3 && opcoes.conservacaoObrigatoria && !form.estadoConservacao) {
        erros.estadoConservacao = 'Escolha o estado de conservação do imóvel.';
    }

    if (passo === consentPasso && !form.consentimento) {
        erros.consentimento = 'Precisamos do seu aceite para continuar.';
    }

    if (passo === 4) {
        if (!form.intencao) erros.intencao = 'Escolha uma das duas opções.';
        if (!form.nome.trim() || form.nome.trim().length < 2) erros.nome = 'Informe seu nome.';
        const tel = soDigitos(form.telefone);
        if (!tel) erros.telefone = 'Informe seu WhatsApp com DDD.';
        else if (tel.length < 10 || tel.length > 13) erros.telefone = 'Confira o número: use DDD + número.';
        if (form.email.trim() && !emailValido(form.email)) erros.email = 'Confira o e-mail digitado.';
    }

    return erros;
}

/* ── payload ────────────────────────────────────────────────────────────────── */

export interface MontarPayloadArgs {
    form: AvmForm;
    codsite: number;
    token: string;
    uf: string | null;
    utms: Record<string, string | null>;
}

const vazioOuNulo = (v: string, max = 120): string | null => {
    const t = String(v || '').trim();
    return t ? t.slice(0, max) : null;
};

export function montarPayload({ form, codsite, token, uf, utms }: MontarPayloadArgs): AvmPayload {
    const telefone = soDigitos(form.telefone);
    const aplicaElevador = pedeElevador(form.tipoNome);

    return {
        codsite,
        token,
        __hp: form.__hp || '',
        // A calculadora estima VALOR DE VENDA (o app responde SO_VENDA fora disso).
        transacao: 'venda',
        tipoId: form.tipoId ? Number(form.tipoId) : null,
        tipoNome: vazioOuNulo(form.tipoNome),
        cidadeId: form.cidadeId ? Number(form.cidadeId) : null,
        cidadeNome: vazioOuNulo(form.cidadeNome),
        uf: uf ? uf.toUpperCase().slice(0, 2) : null,
        bairroId: form.bairroId ? Number(form.bairroId) : null,
        bairroNome: vazioOuNulo(form.bairroNome),
        endereco: vazioOuNulo(form.endereco),
        numero: vazioOuNulo(form.numero, 20),
        complemento: vazioOuNulo(form.complemento, 60),
        cep: soDigitos(form.cep).length === 8 ? soDigitos(form.cep) : null,
        areaPrivativa: numeroOuNulo(form.areaPrivativa),
        areaTerreno: pedeAreaTerreno(form.tipoNome) ? numeroOuNulo(form.areaTerreno) : null,
        dormitorios: form.dormitorios || null,
        suites: form.suites || null,
        banheiros: form.banheiros || null,
        vagas: form.vagas || null,
        elevador: aplicaElevador ? !!form.elevador : null,
        anoConstrucao: form.anoConstrucao.trim() ? parseInt(form.anoConstrucao, 10) : null,
        // O wizard não pergunta o andar (um campo a menos); o contrato aceita null.
        andar: null,
        caracteristicas: form.caracteristicas.slice(),
        // só vai no corpo quando o site usa o catálogo do CRM (os outros ~300 sites mandam o mesmo de antes)
        ...(form.caracteristicaIds.length ? { caracteristicaIds: form.caracteristicaIds.slice(0, AVM_MAX_CARACTERISTICAS) } : {}),
        estadoConservacao: form.estadoConservacao || null,
        precoDesejado: moedaParaNumero(form.precoDesejado),
        intencao: (form.intencao || 'avaliar_profissional') as AvmIntencao,
        lead: {
            nome: form.nome.trim().slice(0, 120),
            telefone,
            email: vazioOuNulo(form.email, 120),
            consentimento: !!form.consentimento,
        },
        utm_source: utms.utm_source ?? null,
        utm_medium: utms.utm_medium ?? null,
        utm_campaign: utms.utm_campaign ?? null,
        utm_id: utms.utm_id ?? null,
    };
}

/* ── WhatsApp da imobiliária (mesma fonte do botão flutuante) ───────────────── */

/**
 * Telefone do site: prioriza os marcados como WhatsApp em `Dados.telN_whats` e, sem
 * nenhum marcado, cai no mesmo encadeamento do FAB (tel1 → tel2 → tel3 → tel4).
 */
export function telefoneWhatsappDoSite(site: any): string {
    const dados = site?.Dados || {};
    const marcados = ['tel1', 'tel2', 'tel3', 'tel4'].filter((k) => dados[`${k}_whats`]);
    const ordem = marcados.length ? marcados : ['tel1', 'tel2', 'tel3', 'tel4'];
    for (const chave of ordem) {
        const d = soDigitos(dados[chave]);
        if (d.length >= 10) return d;
    }
    return '';
}

export function mensagemWhatsapp(valorSugerido: number | null | undefined): string {
    if (valorSugerido && Number.isFinite(Number(valorSugerido))) {
        return `Olá! Fiz a avaliação do meu imóvel no site e a estimativa foi ${formatarBRL(valorSugerido)}. Quero conversar.`;
    }
    return 'Olá! Fiz a avaliação do meu imóvel no site e gostaria de falar com um corretor.';
}

/* ── cores derivadas (sem color-mix: navegador sem suporte descartaria a regra) ── */

/** "#0b3d5c" → "rgba(11, 61, 92, 0.08)". Fora do formato hex/rgb, devolve o fallback. */
export function corComAlfa(cor: string, alfa: number, fallback = 'rgba(15, 23, 42, 0.08)'): string {
    const v = String(cor || '').trim();
    let r = -1;
    let g = -1;
    let b = -1;
    if (/^#[0-9a-f]{3,8}$/i.test(v)) {
        let hex = v.slice(1);
        if (hex.length === 3 || hex.length === 4) {
            hex = hex.slice(0, 3).split('').map((c) => c + c).join('');
        } else if (hex.length >= 6) {
            hex = hex.slice(0, 6);
        }
        if (hex.length === 6) {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
    } else {
        const m = v.match(/^rgba?\(([^)]+)\)$/i);
        if (m) {
            const partes = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
            if (partes.length >= 3) {
                r = partes[0];
                g = partes[1];
                b = partes[2];
            }
        }
    }
    if (!Number.isFinite(r) || r < 0 || !Number.isFinite(g) || g < 0 || !Number.isFinite(b) || b < 0) {
        return fallback;
    }
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alfa})`;
}
